// 세션 녹화(설계서 §9.7) — 화면 녹화 + 이벤트 기록.
//
// 🔴 이 훅의 핵심은 **순서**다(§9.7.1):
//     rec 고지 → 에이전트가 화면에 표시 → rok 확인 → **그제서야** MediaRecorder 시작
//   먼저 녹화를 시작하면 `ctrl` 채널이 화질 반영 확인으로 최대 2초 밀리는 동안
//   **고지 없이 녹화되는 구간**이 생긴다. 확인이 안 오면 녹화하지 않는다.
//
// 재인코딩은 하지 않는다 — 뷰어가 이미 들고 있는 MediaStream 을 그대로 담는다(CPU 부담 없음).

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { saveFileToDisk, saveNoticeOf, type SaveNotice } from '../download';
import type { QualityChannel } from '../quality/channel';
import {
  MAX_RECORDING_BYTES,
  elapsedLabel,
  humanBytes,
  pickRecordingMime,
  recordingFileName,
  timelineJson,
  type RecordingEvent,
  type RecordingEventKind,
} from './protocol';

export interface RecordingState {
  /** 이 세션에서 녹화를 쓸 수 있는가(브라우저 지원 + 에이전트가 고지를 표시할 수 있음). */
  available: boolean;
  /** 지금 녹화 중인가. */
  active: boolean;
  /** 고지를 보내고 확인을 기다리는 중(단추는 "녹화 준비 중…"). */
  preparing: boolean;
  /** 녹화 경과 `12:34`. */
  elapsed: string;
  /** 지금까지 쌓인 크기(사람이 읽는 값). */
  size: string;
  /** 마지막 안내 — 저장 성공(경로·[폴더 열기])도 여기로 온다. 누르면 사라진다. */
  notice?: SaveNotice;
  clearNotice(): void;
  toggle(): void;
  /** 세션 중 사건을 기록에 남긴다(녹화 중이 아니면 버린다). */
  note(kind: RecordingEventKind, text: string): void;
}

/** 녹화가 불가능한 상태에서도 같은 모양을 돌려준다(호출부가 분기하지 않게). */
const UNAVAILABLE: Omit<RecordingState, 'clearNotice' | 'toggle' | 'note'> = {
  available: false,
  active: false,
  preparing: false,
  elapsed: '00:00',
  size: '0B',
};

export function useRecording(
  stream: MediaStream | null,
  channel: QualityChannel | null,
  deviceLabel: string,
): RecordingState {
  const [active, setActive] = useState(false);
  const [preparing, setPreparing] = useState(false);
  const [notice, setNotice] = useState<SaveNotice | undefined>();
  const [bytes, setBytes] = useState(0);
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const [now, setNow] = useState(Date.now());

  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const eventsRef = useRef<RecordingEvent[]>([]);
  const startedRef = useRef<number | null>(null);
  const bytesRef = useRef(0);
  const deviceRef = useRef(deviceLabel);
  deviceRef.current = deviceLabel;

  // 브라우저가 이 컨테이너를 쓸 수 있는가(한 번만 판단).
  const mime = useMemo(
    () =>
      typeof MediaRecorder === 'undefined'
        ? null
        : pickRecordingMime((m) => MediaRecorder.isTypeSupported(m)),
    [],
  );

  const available = mime !== null && stream !== null && channel?.canNotifyRecording === true;

  /** 경과 시간 표시용 시계(녹화 중에만 돈다). */
  useEffect(() => {
    if (!active) {
      return;
    }
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [active]);

  /** 모아 둔 조각을 파일로 내린다. 🔴 어떤 경로로 끝나든(수동 중지·상한·세션 종료) 여기를 지난다. */
  const flushToDisk = useCallback(() => {
    const chunks = chunksRef.current;
    chunksRef.current = [];
    const started = startedRef.current;
    startedRef.current = null;
    if (chunks.length === 0 || started === null) {
      return;
    }
    const at = new Date(started);
    const durationSec = Math.round((Date.now() - started) / 100) / 10;
    // 🔴 저장은 `saveFileToDisk` 한 곳으로만 한다 — 앱(WebView2)에서는 `<a download>` 가 **조용히
    //    아무 일도 하지 않아** 녹화를 통째로 잃는다(2026-09-23 앱 실기에서 확인).
    const timeline = timelineJson({
      startedAt: at.toISOString(),
      device: deviceRef.current,
      durationSec,
      events: eventsRef.current,
    });
    eventsRef.current = [];
    void (async () => {
      const video = await saveFileToDisk(
        recordingFileName(deviceRef.current, at, 'webm'),
        new Blob(chunks, { type: mime ?? 'video/webm' }),
        mime ?? 'video/webm',
      );
      // 이벤트 기록(§9.7.5) — 영상과 같은 이름, 확장자만 다르다.
      const meta = await saveFileToDisk(
        recordingFileName(deviceRef.current, at, 'json'),
        timeline,
        'application/json;charset=utf-8',
      );
      // 🔴 성공도 알린다 — 경로와 [폴더 열기]까지(2026-09-24 Mac 실기: "어디 저장됐는지 모른다").
      //    영상이 실패하면 그것을, 영상은 됐는데 기록(json)이 실패하면 그것을 알린다.
      setNotice(saveNoticeOf('녹화를', !video.ok ? video : !meta.ok ? meta : video));
    })();
  }, [mime]);

  /** 녹화기를 멈춘다(저장은 `onstop` 에서 일어난다). 고지 해제는 호출부가 판단한다. */
  const stopRecorder = useCallback(() => {
    const rec = recorderRef.current;
    recorderRef.current = null;
    if (rec && rec.state !== 'inactive') {
      rec.stop(); // onstop → flushToDisk
    } else {
      flushToDisk();
    }
    setActive(false);
    setStartedAt(null);
  }, [flushToDisk]);

  const startRecording = useCallback(async () => {
    if (!stream || !channel || !mime || recorderRef.current) {
      return;
    }
    setPreparing(true);
    setNotice(undefined);
    // 🔴 ①고지를 보내고 ②표시됐다는 확인을 받은 **뒤에** ③녹화를 시작한다(§9.7.1).
    const notified = await channel.notifyRecording(true);
    if (!notified) {
      setPreparing(false);
      setNotice({ kind: 'error', text: '상대 기기에 녹화 사실을 알리지 못해 녹화를 시작하지 않았습니다.' });
      return;
    }
    let rec: MediaRecorder;
    try {
      rec = new MediaRecorder(stream, { mimeType: mime });
    } catch {
      setPreparing(false);
      setNotice({ kind: 'error', text: '이 브라우저에서 녹화를 시작할 수 없습니다.' });
      void channel.notifyRecording(false); // 켜 둔 고지를 되돌린다
      return;
    }
    chunksRef.current = [];
    eventsRef.current = [];
    bytesRef.current = 0;
    setBytes(0);
    rec.ondataavailable = (ev) => {
      if (!ev.data || ev.data.size === 0) {
        return;
      }
      chunksRef.current.push(ev.data);
      bytesRef.current += ev.data.size;
      setBytes(bytesRef.current);
      // 🔴 상한을 넘으면 스스로 멈춘다 — 조각이 메모리에 쌓이므로 그냥 두면 탭이 죽고 전부 잃는다.
      if (bytesRef.current >= MAX_RECORDING_BYTES) {
        setNotice({ kind: 'ok', text: `녹화가 ${humanBytes(MAX_RECORDING_BYTES)}에 도달해 저장하고 멈췄습니다.` });
        stopRecorder();
        void channel.notifyRecording(false);
      }
    };
    rec.onstop = () => flushToDisk();
    // 1초마다 조각을 받는다 — 브라우저가 죽더라도 받아 둔 만큼은 메모리에 있다.
    rec.start(1000);
    recorderRef.current = rec;
    startedRef.current = Date.now();
    setStartedAt(startedRef.current);
    setPreparing(false);
    setActive(true);
  }, [stream, channel, mime, flushToDisk, stopRecorder]);

  const stopRecording = useCallback(() => {
    stopRecorder();
    void channel?.notifyRecording(false);
  }, [stopRecorder, channel]);

  const toggle = useCallback(() => {
    if (preparing) {
      return;
    }
    if (recorderRef.current) {
      stopRecording();
    } else {
      void startRecording();
    }
  }, [preparing, startRecording, stopRecording]);

  const note = useCallback((kind: RecordingEventKind, text: string) => {
    const started = startedRef.current;
    if (started === null) {
      return; // 녹화 중이 아니면 남기지 않는다(세션 전체 로그가 아니라 **녹화의** 기록이다)
    }
    eventsRef.current.push({
      t: Math.round((Date.now() - started) / 100) / 10,
      kind,
      text,
    });
  }, []);

  /**
   * 🔴 세션이 끝나도 파일은 남긴다(§9.7.4) — 연결 종료·창 닫기·컴포넌트 해제 어느 쪽이든.
   * 녹화가 "끝까지 잘 끝났을 때만" 쓸 수 있으면 쓸모가 없다.
   */
  useEffect(() => {
    return () => {
      if (recorderRef.current) {
        const rec = recorderRef.current;
        recorderRef.current = null;
        if (rec.state !== 'inactive') {
          rec.stop();
        } else {
          flushToDisk();
        }
      }
    };
  }, [flushToDisk]);

  /** 스트림이 끊기면(에이전트 이탈) 녹화도 닫는다 — 빈 화면을 계속 담지 않는다. */
  useEffect(() => {
    if (!active || !stream) {
      return;
    }
    const track = stream.getVideoTracks()[0];
    if (!track) {
      return;
    }
    const onEnded = () => {
      // 저장이 끝나면 경로가 담긴 안내로 바뀐다(flushToDisk). 그 전까지 무슨 일인지 보이게.
      setNotice({ kind: 'ok', text: '연결이 끝나 녹화를 저장하는 중입니다…' });
      stopRecorder();
    };
    track.addEventListener('ended', onEnded);
    return () => track.removeEventListener('ended', onEnded);
  }, [active, stream, stopRecorder]);

  const clearNotice = useCallback(() => setNotice(undefined), []);

  if (!available) {
    return { ...UNAVAILABLE, notice, clearNotice, toggle: () => {}, note };
  }
  return {
    available,
    active,
    preparing,
    elapsed: elapsedLabel(startedAt === null ? 0 : (now - startedAt) / 1000),
    size: humanBytes(bytes),
    notice,
    clearNotice,
    toggle,
    note,
  };
}
